import type { RfqExcelFormData, RfqExcelItem } from './rfq-excel.types';

/** Validate-only PartFinder match statuses for RFQ import items. */
export type RfqPartFinderResolveStatus =
  | 'matched'
  | 'unresolved'
  | 'ambiguous'
  | 'not_searched';

export type RfqPartFinderMatchedBy =
  | 'partNumber'
  | 'impaNumber'
  | 'partNumber+impaNumber'
  | 'partNumber+position'
  | 'impaNumber+position'
  | 'partNumber+impaNumber+position';

/** Snapshot of a PartFinder registry row used for read-only matching. */
export interface RfqPartFinderRecord {
  _id: string;
  name?: string;
  partNumber: string;
  /** IMPA — maps from Part.msfNumber (never imoNumber). */
  impaNumber?: string;
  msfNumber?: string;
  position?: string;
  drawingNumber?: string;
  drawing?: string;
  description?: string;
  categoryName?: string;
  brandName?: string;
  modelName?: string;
  categoryId?: string;
  subCategoryId?: string;
  modelId?: string;
}

export interface RfqPartFinderItemResolution {
  itemId: string;
  status: RfqPartFinderResolveStatus;
  matchedPartId?: string;
  matchedPart?: RfqPartFinderRecord;
  matchedBy?: RfqPartFinderMatchedBy;
  warnings: string[];
  /** Diagnostics only — never used for matching. */
  diagnostics?: {
    importedPartNo?: string;
    importedImpaNo?: string;
    importedPositionNo?: string;
    importedDescription?: string;
    candidateCount?: number;
  };
}

export interface ResolveRfqImportPartFinderInput {
  items: RfqExcelItem[];
  parts: RfqPartFinderRecord[];
  /** Resolved RFQ header patch — used only for conflict warnings, never mutated. */
  formPatch?: Partial<RfqExcelFormData>;
}

export interface ResolveRfqImportPartFinderResult {
  /** Same item object references / values — never mutated. */
  items: RfqExcelItem[];
  resolutions: RfqPartFinderItemResolution[];
  warnings: string[];
}

/** Identifier normalization: trim + case-insensitive + collapse spaces/punct. */
export function normalizePartFinderKey(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_./\\-]+/g, ' ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function partImpa(part: RfqPartFinderRecord): string {
  return part.impaNumber || part.msfNumber || '';
}

function findByPartNumber(
  parts: RfqPartFinderRecord[],
  partNo: string
): RfqPartFinderRecord[] {
  const key = normalizePartFinderKey(partNo);
  if (!key) return [];
  return parts.filter((p) => normalizePartFinderKey(p.partNumber) === key);
}

function findByImpa(parts: RfqPartFinderRecord[], impaNo: string): RfqPartFinderRecord[] {
  const key = normalizePartFinderKey(impaNo);
  if (!key) return [];
  return parts.filter((p) => normalizePartFinderKey(partImpa(p)) === key);
}

function intersectById(
  a: RfqPartFinderRecord[],
  b: RfqPartFinderRecord[]
): RfqPartFinderRecord[] {
  const ids = new Set(b.map((p) => p._id));
  return a.filter((p) => ids.has(p._id));
}

function headerConflictWarnings(
  part: RfqPartFinderRecord,
  formPatch: Partial<RfqExcelFormData> | undefined,
  itemLabel: string
): string[] {
  if (!formPatch) return [];
  const warnings: string[] = [];
  const checks: Array<{ header?: string; partVal?: string; label: string }> = [
    { header: formPatch.category, partVal: part.categoryName, label: 'category' },
    { header: formPatch.brand, partVal: part.brandName, label: 'brand' },
    { header: formPatch.model, partVal: part.modelName, label: 'model' },
  ];
  for (const c of checks) {
    if (!c.header?.trim() || !c.partVal?.trim()) continue;
    if (normalizePartFinderKey(c.header) === normalizePartFinderKey(c.partVal)) continue;
    warnings.push(
      `${itemLabel}: PartFinder match has a different ${c.label} ("${c.partVal}") from the imported RFQ header ("${c.header}").`
    );
  }
  return warnings;
}

function finalizeUnique(
  candidates: RfqPartFinderRecord[],
  positionNo: string,
  baseMatchedBy: RfqPartFinderMatchedBy
): {
  status: RfqPartFinderResolveStatus;
  matched?: RfqPartFinderRecord;
  matchedBy?: RfqPartFinderMatchedBy;
  warnings: string[];
  candidateCount: number;
} {
  const warnings: string[] = [];

  if (candidates.length === 0) {
    return { status: 'unresolved', warnings, candidateCount: 0 };
  }

  if (candidates.length === 1) {
    return {
      status: 'matched',
      matched: candidates[0],
      matchedBy: baseMatchedBy,
      warnings,
      candidateCount: 1,
    };
  }

  // Multiple candidates — position may disambiguate (never primary key alone)
  if (positionNo.trim()) {
    const key = normalizePartFinderKey(positionNo);
    const exactPos = candidates.filter(
      (p) => normalizePartFinderKey(p.position || '') === key
    );
    if (exactPos.length === 1) {
      const matchedBy: RfqPartFinderMatchedBy =
        baseMatchedBy === 'partNumber'
          ? 'partNumber+position'
          : baseMatchedBy === 'impaNumber'
            ? 'impaNumber+position'
            : 'partNumber+impaNumber+position';
      return {
        status: 'matched',
        matched: exactPos[0],
        matchedBy,
        warnings,
        candidateCount: candidates.length,
      };
    }
    if (exactPos.length > 1) {
      return {
        status: 'ambiguous',
        warnings: [
          `Multiple PartFinder records still match after position "${positionNo.trim()}".`,
        ],
        candidateCount: exactPos.length,
      };
    }
    return {
      status: 'ambiguous',
      warnings: [
        `Multiple PartFinder candidates found; position "${positionNo.trim()}" did not uniquely disambiguate.`,
      ],
      candidateCount: candidates.length,
    };
  }

  return {
    status: 'ambiguous',
    warnings: [`Multiple PartFinder records match (${candidates.length} candidates).`],
    candidateCount: candidates.length,
  };
}

function resolveOneItem(
  item: RfqExcelItem,
  parts: RfqPartFinderRecord[],
  formPatch: Partial<RfqExcelFormData> | undefined,
  index: number
): RfqPartFinderItemResolution {
  const itemId = item.id || String(index + 1);
  // Row position, never item.id — Excel item ids are internal UUIDs.
  const itemLabel = `Item ${index + 1}`;
  const partNo = (item.partNo || '').trim();
  const impaNo = (item.impaNo || '').trim();
  const positionNo = (item.positionNo || '').trim();

  const diagnostics = {
    importedPartNo: partNo || undefined,
    importedImpaNo: impaNo || undefined,
    importedPositionNo: positionNo || undefined,
    importedDescription: item.itemDescription?.trim() || undefined,
  };

  // modelSerial / description / drawing / position-alone never trigger search
  if (!partNo && !impaNo) {
    return {
      itemId,
      status: 'not_searched',
      warnings: [],
      diagnostics,
    };
  }

  const byPart = partNo ? findByPartNumber(parts, partNo) : [];
  const byImpa = impaNo ? findByImpa(parts, impaNo) : [];

  let result: ReturnType<typeof finalizeUnique>;

  if (partNo && impaNo) {
    if (byPart.length === 0 && byImpa.length === 0) {
      return {
        itemId,
        status: 'unresolved',
        warnings: [
          `${itemLabel}: Part Number "${partNo}" and IMPA "${impaNo}" not found in PartFinder.`,
        ],
        diagnostics: { ...diagnostics, candidateCount: 0 },
      };
    }

    if (byPart.length >= 1 && byImpa.length >= 1) {
      const both = intersectById(byPart, byImpa);
      if (both.length === 0) {
        return {
          itemId,
          status: 'ambiguous',
          warnings: [
            `${itemLabel}: Part Number and IMPA Number resolve to different PartFinder records.`,
          ],
          diagnostics: {
            ...diagnostics,
            candidateCount: byPart.length + byImpa.length,
          },
        };
      }
      result = finalizeUnique(both, positionNo, 'partNumber+impaNumber');
    } else if (byPart.length >= 1 && byImpa.length === 0) {
      return {
        itemId,
        status: 'ambiguous',
        warnings: [
          `${itemLabel}: Part Number matched PartFinder but IMPA "${impaNo}" did not — not assuming either is wrong.`,
        ],
        diagnostics: { ...diagnostics, candidateCount: byPart.length },
      };
    } else {
      // byImpa >= 1, byPart === 0
      return {
        itemId,
        status: 'ambiguous',
        warnings: [
          `${itemLabel}: IMPA matched PartFinder but Part Number "${partNo}" did not — not assuming either is wrong.`,
        ],
        diagnostics: { ...diagnostics, candidateCount: byImpa.length },
      };
    }
  } else if (partNo) {
    result = finalizeUnique(byPart, positionNo, 'partNumber');
    if (result.status === 'unresolved') {
      result.warnings = [
        `${itemLabel}: Part Number "${partNo}" not found in PartFinder.`,
        ...result.warnings,
      ];
    } else if (result.status === 'ambiguous' && result.warnings.length === 0) {
      result.warnings = [
        `${itemLabel}: Multiple PartFinder records match Part Number "${partNo}".`,
      ];
    }
  } else {
    result = finalizeUnique(byImpa, positionNo, 'impaNumber');
    if (result.status === 'unresolved') {
      result.warnings = [
        `${itemLabel}: IMPA "${impaNo}" not found in PartFinder.`,
        ...result.warnings,
      ];
    } else if (result.status === 'ambiguous' && result.warnings.length === 0) {
      result.warnings = [
        `${itemLabel}: Multiple PartFinder records match IMPA "${impaNo}".`,
      ];
    }
  }

  const warnings = [...result.warnings];
  if (result.status === 'matched' && result.matched) {
    warnings.push(...headerConflictWarnings(result.matched, formPatch, itemLabel));
    // Prefix item label on generic ambiguous messages from finalizeUnique
    const prefixed = warnings.map((w) =>
      w.startsWith('Item ') || w.startsWith(itemLabel) ? w : `${itemLabel}: ${w}`
    );
    return {
      itemId,
      status: 'matched',
      matchedPartId: result.matched._id,
      matchedPart: result.matched,
      matchedBy: result.matchedBy,
      warnings: prefixed,
      diagnostics: { ...diagnostics, candidateCount: result.candidateCount },
    };
  }

  const prefixed = warnings.map((w) =>
    w.startsWith('Item ') || w.startsWith(itemLabel) ? w : `${itemLabel}: ${w}`
  );

  return {
    itemId,
    status: result.status,
    matchedBy: result.matchedBy,
    warnings: prefixed,
    diagnostics: { ...diagnostics, candidateCount: result.candidateCount },
  };
}

/**
 * Read-only PartFinder validation for imported RFQ line items.
 * Never mutates items, never writes PartFinder records, never calls intelligence mutations.
 */
export function resolveRfqImportPartFinder(
  input: ResolveRfqImportPartFinderInput
): ResolveRfqImportPartFinderResult {
  const parts = input.parts ?? [];
  const resolutions = input.items.map((item, index) =>
    resolveOneItem(item, parts, input.formPatch, index)
  );
  const warnings = resolutions.flatMap((r) => r.warnings);

  // Guarantee item values are unchanged (return same array reference)
  return {
    items: input.items,
    resolutions,
    warnings,
  };
}

/** Map admin PartFinder registry API rows into lookup records. */
export function mapPartFinderRegistryRows(rows: unknown[]): RfqPartFinderRecord[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((raw) => {
    const r = raw as Record<string, unknown>;
    return {
      _id: String(r._id ?? ''),
      name: r.name != null ? String(r.name) : undefined,
      partNumber: String(r.partNumber ?? ''),
      impaNumber:
        r.impaNumber != null
          ? String(r.impaNumber)
          : r.msfNumber != null
            ? String(r.msfNumber)
            : '',
      msfNumber: r.msfNumber != null ? String(r.msfNumber) : undefined,
      position: r.position != null ? String(r.position) : '',
      drawingNumber:
        r.drawingNumber != null
          ? String(r.drawingNumber)
          : r.drawing != null
            ? String(r.drawing)
            : undefined,
      description: r.description != null ? String(r.description) : undefined,
      categoryName: r.categoryName != null ? String(r.categoryName) : undefined,
      brandName: r.brandName != null ? String(r.brandName) : undefined,
      modelName: r.modelName != null ? String(r.modelName) : undefined,
      categoryId: r.categoryId != null ? String(r.categoryId) : undefined,
      subCategoryId: r.subCategoryId != null ? String(r.subCategoryId) : undefined,
      modelId: r.modelId != null ? String(r.modelId) : undefined,
    };
  });
}
